import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/api';

const normalizeToken = (token) => {
  if (typeof token !== 'string') return null;
  const trimmed = token.trim();
  if (!trimmed || trimmed === 'undefined' || trimmed === 'null') return null;
  return trimmed;
};

const syncTokenInStorage = (key, token) => {
  if (token) {
    localStorage.setItem(key, token);
    return;
  }
  localStorage.removeItem(key);
};

const storedUser = (() => {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
})();

const initialState = {
  accessToken: normalizeToken(localStorage.getItem('accessToken')),
  refreshToken: normalizeToken(localStorage.getItem('refreshToken')),
  user: storedUser,
  loading: false,
  error: null,
};

// Запрос пин-кода
export const requestPin = createAsyncThunk(
  'auth/requestPin',
  async ({ pseudo }, { rejectWithValue }) => {
    try {
      const res = await api.post('/auth/request-pin', { pseudo });
      return res.data; // Возвращаем только данные
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Ошибка запроса пин-кода');
    }
  }
);


// app.post(`${AUTH_PATH}/request-pin`, authControllers.requestPin);
//   app.post(`${AUTH_PATH}/verify-pin`, authControllers.verifyPin);
//   app.post(`${AUTH_PATH}/change-pseudo`, authControllers.changePseudo);
//   app.get(`${AUTH_PATH}/verify`, authControllers.verifyAuthToken);
// Подтверждение пин-кода или токена
export const verifyPin = createAsyncThunk(
  'auth/verifyPin',
  async ({ pseudo, pin, tempToken }, { rejectWithValue }) => {
    try {
      let res;
      if (tempToken) {
        res = await api.get(`/auth/verify?token=${tempToken}`);
      } else {
         res = await api.post('/auth/verify-pin', { pseudo, pin });
      }

      return res.data; // Возвращаем только данные
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Ошибка подтверждения');
    }
  }
);

// Обновление токена
export const refreshAuthToken = createAsyncThunk(
  'auth/refreshToken',
  async (refreshToken, { rejectWithValue }) => {
    try {
      const safeRefreshToken = normalizeToken(refreshToken) || normalizeToken(localStorage.getItem('refreshToken'));
      if (!safeRefreshToken) {
        return rejectWithValue('Refresh token отсутствует');
      }
      const response = await api.post('/auth/refresh', { refreshToken: safeRefreshToken });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка обновления токена');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { rejectWithValue }) => {
    try {
      await api.post('/auth/logout');
      return true;
    } catch (error) {
      const message = error.response?.data?.message || 'Ошибка выхода';
      return rejectWithValue(message);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout(state) {
      state.accessToken = null;
      state.refreshToken = null;
      state.user = null;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    },
    setTokens(state, action) {
      const nextAccessToken = normalizeToken(action.payload?.accessToken);
      const nextRefreshToken = normalizeToken(action.payload?.refreshToken) || state.refreshToken;

      state.accessToken = nextAccessToken;
      state.refreshToken = nextRefreshToken;

      syncTokenInStorage('accessToken', nextAccessToken);
      syncTokenInStorage('refreshToken', nextRefreshToken);

      if (action.payload.user) {
        state.user = action.payload.user;
        localStorage.setItem('user', JSON.stringify(action.payload.user));
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Обработка запроса пин-кода
      .addCase(requestPin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestPin.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(requestPin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Обработка подтверждения пин-кода или токена
      .addCase(verifyPin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyPin.fulfilled, (state, action) => {
        state.loading = false;
        const nextAccessToken = normalizeToken(action.payload?.accessToken);
        const nextRefreshToken = normalizeToken(action.payload?.refreshToken);

        state.accessToken = nextAccessToken;
        state.refreshToken = nextRefreshToken;

        syncTokenInStorage('accessToken', nextAccessToken);
        syncTokenInStorage('refreshToken', nextRefreshToken);

        if (action.payload.user) {
          state.user = action.payload.user;
          localStorage.setItem('user', JSON.stringify(action.payload.user));
        }
      })
      .addCase(verifyPin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Обработка обновления токена
      .addCase(refreshAuthToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshAuthToken.fulfilled, (state, action) => {
        state.loading = false;
        const nextAccessToken = normalizeToken(action.payload?.accessToken);
        const nextRefreshToken = normalizeToken(action.payload?.refreshToken) || state.refreshToken;

        state.accessToken = nextAccessToken;
        state.refreshToken = nextRefreshToken;

        syncTokenInStorage('accessToken', nextAccessToken);
        syncTokenInStorage('refreshToken', nextRefreshToken);

        if (action.payload.user) {
          state.user = action.payload.user;
          localStorage.setItem('user', JSON.stringify(action.payload.user));
        }
      })
      .addCase(refreshAuthToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.accessToken = null;
        state.refreshToken = null;
        state.user = null;
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
      })
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.accessToken = null;
        state.refreshToken = null;
        state.user = null;
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.accessToken = null;
        state.refreshToken = null;
        state.user = null;
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
      });
  },
});

export const { logout, setTokens } = authSlice.actions;

// Selector exported after slice creation to avoid initialization errors
export const selectIsAuthenticated = (state) => !!state.auth.accessToken;

export default authSlice.reducer;
